/**
 * Self-service rename login — costs 1 EP (or EP1).
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usersAPI } from '../api/users';
import { useAuthStore } from '../store/auth';
import { ThemedPageHeader } from '../components/ui/ThemedPageHeader';
import {
  THEMED_SURFACE_COLORS as C,
  ThemedField,
  ThemedInput,
  ThemedNotice,
  ThemedPanel,
  ThemedSubmitButton,
  ThemedBottomActionBar,
} from '../components/ui/ThemedSurface';
import { TransactionSecurity } from '../components/ui/TransactionSecurity';

export function RenameUsernamePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const profile = useAuthStore((s) => s.profile);
  const setProfile = useAuthStore((s) => s.setProfile);
  const [newUsername, setNewUsername] = useState('');
  const [txPassword, setTxPassword] = useState('');
  const [securityAnswer, setSecurityAnswer] = useState('');
  const [showSecurity, setShowSecurity] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const disabled = !newUsername.trim() || !txPassword || submitting;

  async function handleSubmit() {
    setError('');
    setSuccess('');
    setSubmitting(true);
    try {
      const data = await usersAPI.renameUsername({
        new_username: newUsername.trim(),
        transaction_password: txPassword,
      });
      if (profile) {
        setProfile({ ...profile, username: data.username });
      }
      setSuccess(t('renameUsername.successMsg'));
      setNewUsername('');
      setTxPassword('');
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'message' in e
          ? String((e as { message: string }).message)
          : t('common.error');
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="relative min-h-full" style={{ background: C.pageBg }}>
      <div className="relative mx-auto min-h-full w-full max-w-[430px]">
        <ThemedPageHeader title={t('renameUsername.title')} onBack={() => navigate(-1)} theme={C} />
        <div
          className="flex flex-col gap-4 overflow-y-auto px-5 pt-[70px]"
          style={{ height: 'calc(100% - 70px)', paddingBottom: '112px' }}
        >
          <ThemedPanel className="p-4">
            <div className="text-sm font-semibold" style={{ color: C.text }}>
              {t('renameUsername.title')}
            </div>
            <div className="mt-2 text-xs leading-relaxed" style={{ color: C.muted }}>
              {t('renameUsername.subtitle')}
            </div>
          </ThemedPanel>

          <ThemedPanel className="p-4">
            <div className="flex flex-col gap-5">
              <ThemedField label={t('renameUsername.current')}>
                <ThemedInput value={profile?.username ?? ''} disabled />
              </ThemedField>
              <ThemedField label={t('renameUsername.new')} required>
                <ThemedInput
                  value={newUsername}
                  placeholder={t('renameUsername.newPlaceholder')}
                  onChange={(e) => setNewUsername(e.target.value)}
                  autoCapitalize="off"
                  autoCorrect="off"
                />
              </ThemedField>
              <TransactionSecurity
                txPassword={txPassword}
                onTxPasswordChange={setTxPassword}
                showSecurity={showSecurity}
                onShowSecurityChange={setShowSecurity}
                securityAnswer={securityAnswer}
                onSecurityAnswerChange={setSecurityAnswer}
                colors={C}
              />
              {error ? <ThemedNotice tone="error">{error}</ThemedNotice> : null}
              {success ? <ThemedNotice tone="success">{success}</ThemedNotice> : null}
            </div>
          </ThemedPanel>
        </div>

        <ThemedBottomActionBar>
          <ThemedSubmitButton disabled={disabled} onClick={() => void handleSubmit()}>
            {submitting ? '...' : t('renameUsername.submit')}
          </ThemedSubmitButton>
        </ThemedBottomActionBar>
      </div>
    </div>
  );
}

export default RenameUsernamePage;
